// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';

import {
  getActivePanelId,
  getEnabledMediaEnginesCount,
  getIsMediaEngineTestPending,
  getMediaEngines,
  getMediaEngineTotalSeatCount,
} from 'state/selectors';
import { bindActionCreatorsSafe } from 'util/safeDispatch';

import type { Dispatch, MediaEngine } from 'state/types';
import PageHeader from '../PageHeader';
import localizedStrings from 'util/localizedStrings';
import MediaEngineRow from './MediaEngineRow';
import CreateMediaEngineDialog from './CreateMediaEngineDialog';
import {
  CREATE_MEDIA_ENGINE_PANEL_ID,
  MEDIA_ENGINE_TYPE_BUILT_IN,
} from 'util/constants';
import { actions } from 'portfolio-shared/state/reducers/dialogPanelReducer';
import {
  deleteMediaEngine,
  testMediaEngine,
  updateMediaEngine,
} from 'state/commands/mediaEngineCommands';
import { showSingleButtonAlert } from 'portfolio-shared/state/reducers/modalPromptReducer';
const { showDialogPanel } = actions;

type Props = {
  activePanelId: string,
  deleteMediaEngine: typeof deleteMediaEngine,
  enabledMediaEnginesCount: number,
  intl: IntlShape,
  isMediaEngineTestPending: boolean,
  mediaEngines: MediaEngine[],
  mediaEngineTotalSeatCount: number,
  showDialogPanel: typeof showDialogPanel,
  showSingleButtonAlert: typeof showSingleButtonAlert,
  testMediaEngine: typeof testMediaEngine,
  updateMediaEngine: typeof updateMediaEngine,
};

type State = {
  selectedMediaEngine: ?MediaEngine,
};

class MediaEnginesPage extends Component<Props, State> {
  handleClickActive: Function;
  handleClickAdd: Function;
  handleClickDelete: Function;
  handleSelectRow: Function;
  handleTest: Function;

  state = { selectedMediaEngine: undefined };

  constructor() {
    super();
    this.handleClickActive = this.handleClickActive.bind(this);
    this.handleClickAdd = this.handleClickAdd.bind(this);
    this.handleClickDelete = this.handleClickDelete.bind(this);
    this.handleSelectRow = this.handleSelectRow.bind(this);
    this.handleTest = this.handleTest.bind(this);
  }

  handleSelectRow(e: SyntheticMouseEvent<>, index: number) {
    this.setState({ selectedMediaEngine: this.props.mediaEngines[index] });
  }

  handleClickActive(me: MediaEngine) {
    const { mediaEngines, mediaEngineTotalSeatCount } = this.props;
    const enabledMEs = mediaEngines.filter(me => me.enabled);

    if (!me.enabled) {
      // disallow enabling more than the seat count
      if (enabledMEs.length >= mediaEngineTotalSeatCount) {
        return;
      }
    } else {
      // show an alert if user has disabled all MEs
      if (enabledMEs.length === 1) {
        this.props.showSingleButtonAlert(
          localizedStrings.MEDIA_ENGINES_WARN_NO_ENGINES_ARE_ACTIVE
        );
      }
    }
    const errorMsg = this.props.intl.formatMessage(
      localizedStrings.MEDIA_ENGINE_NOT_OPERATIONAL
    );
    this.props.updateMediaEngine(
      {
        ...me,
        enabled: !me.enabled,
      },
      errorMsg
    );
  }

  handleClickAdd() {
    this.props.showDialogPanel(CREATE_MEDIA_ENGINE_PANEL_ID);
  }

  handleClickDelete() {
    const me = this.state.selectedMediaEngine;
    if (me) {
      this.props.deleteMediaEngine(me.id);
    }
  }

  handleTest(me: MediaEngine) {
    this.props.testMediaEngine(
      me,
      this.props.intl.formatMessage(localizedStrings.MEDIA_ENGINE_OPERATIONAL),
      this.props.intl.formatMessage(
        localizedStrings.MEDIA_ENGINE_NOT_OPERATIONAL
      )
    );
  }

  render() {
    const {
      activePanelId,
      enabledMediaEnginesCount,
      intl,
      isMediaEngineTestPending,
      mediaEngines,
      mediaEngineTotalSeatCount,
    } = this.props;
    const { selectedMediaEngine } = this.state;
    const deleteDisabled =
      !this.state.selectedMediaEngine ||
      this.state.selectedMediaEngine.type === MEDIA_ENGINE_TYPE_BUILT_IN;
    return (
      <div className="page" data-test-name="media-engines-page">
        <PageHeader
          deleteDisabled={deleteDisabled}
          onClickAdd={this.handleClickAdd}
          onClickDelete={this.handleClickDelete}
          title={`${intl.formatMessage(
            localizedStrings.NAV_MEDIA_ENGINES
          )} (${enabledMediaEnginesCount}/${mediaEngineTotalSeatCount})`}
        />
        <div className="page-contents">
          <table className="table is-fullwidth is-hoverable">
            <tbody>
              <tr>
                <th>
                  <FormattedMessage
                    {...localizedStrings.MEDIA_ENGINES_COLUMN_ACTIVE}
                  />
                </th>
                <th>
                  <FormattedMessage
                    {...localizedStrings.MEDIA_ENGINES_COLUMN_ADDRESS}
                  />
                </th>
                <th>
                  <FormattedMessage
                    {...localizedStrings.MEDIA_ENGINES_COLUMN_TEST}
                  />
                </th>
              </tr>
              {mediaEngines.map((me, index) => {
                return (
                  <MediaEngineRow
                    key={me.id}
                    index={index}
                    intl={intl}
                    isMediaEngineTestPending={isMediaEngineTestPending}
                    isSelected={
                      !!(
                        selectedMediaEngine && selectedMediaEngine.id === me.id
                      )
                    }
                    mediaEngine={me}
                    onClickRow={this.handleSelectRow}
                    onClickActive={this.handleClickActive}
                    onClickTest={this.handleTest}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
        {activePanelId === CREATE_MEDIA_ENGINE_PANEL_ID && (
          <CreateMediaEngineDialog
            allActivationsUsed={
              enabledMediaEnginesCount >= mediaEngineTotalSeatCount
            }
          />
        )}
      </div>
    );
  }
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    enabledMediaEnginesCount: getEnabledMediaEnginesCount(state),
    isMediaEngineTestPending: getIsMediaEngineTestPending(state),
    mediaEngines: getMediaEngines(state),
    mediaEngineTotalSeatCount: getMediaEngineTotalSeatCount(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      deleteMediaEngine,
      showDialogPanel,
      showSingleButtonAlert,
      testMediaEngine,
      updateMediaEngine,
    })
)(injectIntl(MediaEnginesPage));



// WEBPACK FOOTER //
// ./src/view/Pages/GlobalSettingsPages/MediaEnginesPage.js